import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Breadcrumb from "../../components/services/Breadcrumb/Breadcrumb";
import { Grid, Box, Button } from "@mui/material";
import SectionTitle from "../../components/SectionTitle/SectionTitle";
import DescriptionEditor from "../../components/DescriptionEditor/DescriptionEditor";
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import InputField from "../../components/InputField/InputField";
import { step1ValidationSchema } from '../../utils/validation';
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { requestNewPriceOffer, resetPriceOfferState } from "./../../redux/slices/priceOfferSlice";
import SuccessMessage from "../../components/SuccessSnackbar/SuccessMessage";
import { useNavigate } from 'react-router-dom';

const RequestPricePage = () => {
    const { translations, direction } = useLanguage();
    const t = translations;
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoggedIn, user, token } = useSelector(state => state.login);  // Get user and token from Redux state
    const { loading, success, error } = useSelector(state => state.priceOffer);  // Get priceOffer state from Redux
    const dispatch = useDispatch();
    const [serviceTitle, setServiceTitle] = useState("");
    const [file, setFile] = useState(null);
    const [serviceId, setServiceId] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        address: "",
        description: "",
    });
    // Form validation schema
    const validationSchema = step1ValidationSchema(t, true, false, true);

    // Formik hook
    const formik = useFormik({
        initialValues: {
            name: "",
            phone: "",
            email: "",
            address: "",
            description: "",
        },
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: (values) => {
            handleSubmit(values);
        },
    });
 

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setServiceTitle(params.get("serviceTitle"));
        setServiceId(params.get("serviceId"));
    }, [location]);
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };
    const handleDescriptionChange = (value) => {
        formik.setFieldValue("description", value); // Update Formik's field value directly
        formik.validateField("description"); // Manually trigger validation for this field
    };
    const handleCancel = () => {
        window.scrollTo(0, 0);
        navigate('/');  
    };
    const handleSubmit = (values) => {
        if (!formik.isValid || !formik.dirty) {
            return;
        }
        // Prepare data for the request, as an object directly
        const requestData = {
            ...(user && user.id && { customerFK: user.id }),
            // customerFK: user && user.id ? user.id : "null", 
            name: values.name,
            mobile: values.phone,
            email: values.email,
            unitDescription: values.address,  
            description: values.description.replace(/<p>/g, '').replace(/<\/p>/g, ''),
        };
        // Dispatch the action to make the API call
        dispatch(requestNewPriceOffer({ formData: requestData, token }))
            .unwrap()
            .then((response) => {
              
            })
            .catch((error) => {
                console.error("Error:", error);  // Log error response
            });
    };
    

    const handleReset = () => {
        formik.resetForm();
        dispatch(resetPriceOfferState());  // Reset the price offer state in Redux
    };
    // Form fields
    const formFields = [
        { name: "name", label: t.name, required: true, placeholder: t.enterName },
        { name: "phone", label: t.phoneNumber, required: true, placeholder: t.enterPhoneNumber },
        { name: "email", label: t.email, required: false, placeholder: t.enterEmail },
        { name: "address", label: t.address, required: true, placeholder: t.enterAddress },
    ];
    return (
        <Box >
            <Breadcrumb items={[t.buttonText]} />
            {success ? (
                <SuccessMessage
                    message={t.priceRequestSuccess}
                    onReset={handleReset}
                />
            ) : (
                <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, mb: 4 }}>
                    <Box sx={{
                        p: 3, mb: 6, border: "1px solid #CED4DA", borderRadius: "16px", boxShadow: "0px 4px 4px 0px #00000040"
                    }}>
                        <SectionTitle
                            title={t.requestInformation1}
                            sx={{
                                fontSize: "18px", borderBottom: "1px solid #0000001A",
                                pb: 1,
                            }}
                        />

                        <form onSubmit={formik.handleSubmit}>
                            <Grid container spacing={3} sx={{ direction: "ltr", justifyContent: "center" }}>
                                {/* Dynamically render InputField components */}
                                {formFields.map((field) => (
                                    <Grid item xs={12} sm={6} key={field.name}>
                                        <InputField
                                            name={field.name}
                                            label={field.label}
                                            value={formik.values[field.name]}
                                            onChange={formik.handleChange}
                                            placeholder={field.placeholder}
                                            required={field.required}
                                            type={field.name === "phone" ? "tel" : field.name === "email" ? "email" : "text"}
                                            direction={direction}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                                            helperText={formik.touched[field.name] && formik.errors[field.name]}
                                        />
                                    </Grid>
                                ))}
                                {/* Use DescriptionEditor for the description field */}
                                <DescriptionEditor
                                    title={t.requestInformation.description}
                                    value={formik.values.description}
                                    onChange={handleDescriptionChange}
                                    placeholder={t.descriptionPlaceholder}
                                    height="200px"
                                    t={t}
                                    direction={direction}
                                    onBlur={formik.handleBlur}  // Formik's onBlur should be passed to the editor
                                    error={formik.touched.description && Boolean(formik.errors.description)}
                                    helperText={formik.touched.description && formik.errors.description}
                                    name="description" // Make sure you pass the name of the field for validation
                                />

                            </Grid>
                            {/* Action Buttons */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 2,
                                    mt: 4,
                                }}
                            >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ px: 4, backgroundColor: "#12538B", color: "#fff", fontWeight: 700 }}
                                >
                                    {t.buttons.add}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ px: 4, fontWeight: 700 }}
                                    onClick={() => formik.resetForm()}
                                >
                                    {t.buttons.clear}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    sx={{ px: 4, fontWeight: 700 }}
                                    onClick={handleCancel} 
                                >
                                    {t.buttons.cancel}
                                </Button>
                            </Box>
                        </form>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default RequestPricePage;
